import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Chip, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, Skeleton, alpha, Avatar,
} from '@mui/material';
import { Close, Savings, AccountBalanceWallet } from '@mui/icons-material';
import { C, fmtCurrency } from './shared';
import { apiFetch } from '../../utils/api';

export type TxType = 'deposit' | 'withdrawal';

interface Tx {
  source: string;
  userId: string | null;
  amount: number | null;
  currency: string | null;
  usdAmount: string;
  status: string | null;
  counted: boolean;
  reference: string | null;
  createdAt: string | null;
}

interface TxResponse {
  type: string;
  total: number;
  countedCount: number;
  countedUsd: string;
  transactions: Tx[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  type: TxType;
  from: string | null;
  to: string | null;
  periodLabel: string;
}

// Green when it contributes to the totals, red for hard failures, amber for pending.
const statusColor = (status: string | null, counted: boolean) => {
  if (counted) return C.success;
  const s = (status || '').toLowerCase();
  if (/fail|reject|expire|cancel|declin/.test(s)) return C.error;
  return C.warning;
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const TransactionsModal: React.FC<Props> = ({ open, onClose, type, from, to, periodLabel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TxResponse | null>(null);

  const isDeposit = type === 'deposit';
  const accent = isDeposit ? C.success : C.error;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ type, limit: '200' });
      if (from && to) { qs.set('from', from); qs.set('to', to); }
      const res = await apiFetch<TxResponse>(`/api/admin/dashboard/transactions?${qs.toString()}`);
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load transactions');
      setData(null);
    }
    setLoading(false);
  }, [type, from, to]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 3, backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(accent, 0.15), color: accent, width: 36, height: 36 }}>
              {isDeposit ? <Savings sx={{ fontSize: 20 }} /> : <AccountBalanceWallet sx={{ fontSize: 20 }} />}
            </Avatar>
            <Box>
              <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                {isDeposit ? 'Deposit' : 'Withdrawal'} Details
              </Typography>
              <Typography sx={{ color: C.textMuted, fontSize: '0.75rem' }}>{periodLabel}</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: C.textSecondary, '&:hover': { color: C.text } }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {data && !loading && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Total: ${fmtCurrency(data.countedUsd)}`}
              sx={{ bgcolor: alpha(accent, 0.15), color: accent, fontWeight: 700 }} />
            <Chip size="small" label={`${data.countedCount} successful`}
              sx={{ bgcolor: C.cardHover, color: C.textMuted }} />
            <Chip size="small" label={`${data.total} total records`}
              sx={{ bgcolor: C.cardHover, color: C.textMuted }} />
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ p: 2.5 }}>
            {[...Array(6)].map((_, i) => <Skeleton key={i} height={40} sx={{ bgcolor: C.cardHover, mb: 1 }} />)}
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: C.error, fontSize: '0.85rem' }}>{error}</Typography>
          </Box>
        )}

        {!loading && !error && data && data.transactions.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography sx={{ color: C.textMuted, fontSize: '0.85rem' }}>
              No {isDeposit ? 'deposits' : 'withdrawals'} found for {periodLabel.toLowerCase()}.
            </Typography>
          </Box>
        )}

        {!loading && !error && data && data.transactions.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Date', 'Source', 'User', 'Amount', 'USD Value', 'Status'].map(h => (
                    <TableCell key={h} sx={{
                      bgcolor: C.cardLight, color: C.textMuted, fontWeight: 700, fontSize: '0.7rem',
                      borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
                    }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.transactions.map((t, i) => (
                  <TableRow key={i} sx={{ '&:hover': { bgcolor: C.cardHover } }}>
                    <TableCell sx={{ color: C.textSecondary, fontSize: '0.75rem', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                      {fmtDate(t.createdAt)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: `1px solid ${C.border}` }}>
                      <Chip size="small" label={t.source} sx={{ bgcolor: C.cardHover, color: C.textMuted, fontSize: '0.68rem', height: 20 }} />
                    </TableCell>
                    <TableCell sx={{ color: C.textSecondary, fontSize: '0.72rem', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace' }}>
                      {t.userId || '—'}
                    </TableCell>
                    <TableCell sx={{ color: C.text, fontSize: '0.75rem', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                      {t.amount === null ? '—' : `${t.amount} ${t.currency || ''}`}
                    </TableCell>
                    <TableCell sx={{ color: C.text, fontWeight: 700, fontSize: '0.78rem', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                      {fmtCurrency(t.usdAmount)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: `1px solid ${C.border}` }}>
                      <Chip
                        size="small"
                        label={t.status || '—'}
                        sx={{
                          bgcolor: alpha(statusColor(t.status, t.counted), 0.15),
                          color: statusColor(t.status, t.counted),
                          fontWeight: 600, fontSize: '0.68rem', height: 20, textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ p: 1.5, borderTop: `1px solid ${C.border}` }}>
              <Typography sx={{ color: C.textMuted, fontSize: '0.68rem' }}>
                Only successful transactions (green) count toward the dashboard totals.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionsModal;
